import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';

const MIME_EXTENSIONS = {
  'application/pdf': 'pdf',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'text/csv': 'csv',
  'text/plain': 'txt'
};

const sanitizeFileName = (fileName = 'document') => {
  const cleaned = String(fileName)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);
  return cleaned || 'document';
};

const ensureExtension = (fileName, mimeType) => {
  const cleanName = sanitizeFileName(fileName);
  if (/\.[a-zA-Z0-9]{1,8}$/.test(cleanName)) return cleanName;
  const extension = MIME_EXTENSIONS[String(mimeType || '').split(';')[0].toLowerCase()];
  return extension ? `${cleanName}.${extension}` : cleanName;
};

const normalizeBlob = (value, mimeType) => (
  value instanceof Blob
    ? (value.type || !mimeType ? value : new Blob([value], { type: mimeType }))
    : new Blob([value], { type: mimeType || 'application/octet-stream' })
);

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error || new Error('Unable to read the file.'));
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
  };
  reader.readAsDataURL(blob);
});

export const IN_APP_FILE_PREVIEW_EVENT = 'smtlink:open-file-preview';

let lastWebPreview = { key: '', openedAt: 0 };

const isDuplicateWebPreview = (key) => {
  const openedAt = Date.now();
  const duplicate = lastWebPreview.key === key && openedAt - lastWebPreview.openedAt < 800;
  lastWebPreview = { key, openedAt };
  return duplicate;
};

const requestInAppPreview = (detail) => {
  const event = new CustomEvent(IN_APP_FILE_PREVIEW_EVENT, {
    detail,
    cancelable: true
  });

  // El visor global cancela el evento para confirmar que se hizo cargo del archivo.
  return !window.dispatchEvent(event);
};

const downloadFromUrl = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const openBlobOnWeb = (blob, fileName, download = false) => {
  const objectUrl = window.URL.createObjectURL(blob);

  if (download) {
    downloadFromUrl(objectUrl, fileName);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1_000);
    return { mode: 'download' };
  }

  const previewKey = `${fileName}:${blob.type}:${blob.size}`;
  if (isDuplicateWebPreview(previewKey)) {
    window.URL.revokeObjectURL(objectUrl);
    return { mode: 'deduplicated' };
  }

  const handled = requestInAppPreview({
    url: objectUrl,
    fileName,
    mimeType: blob.type,
    revokeOnClose: true
  });

  if (!handled) {
    // Fallback seguro cuando el visor todavía no está montado: misma pestaña, nunca dos.
    window.location.assign(objectUrl);
    return { mode: 'same-window' };
  }

  return { mode: 'preview' };
};

export const isNativeMobile = () => Capacitor.isNativePlatform();

export const openBlobFile = async (value, options = {}) => {
  const mimeType = options.mimeType || value?.type || 'application/octet-stream';
  const fileName = ensureExtension(options.fileName || 'document', mimeType);
  const blob = normalizeBlob(value, mimeType);

  if (!isNativeMobile()) {
    const result = openBlobOnWeb(blob, fileName, Boolean(options.download));
    return { platform: 'web', fileName, ...result };
  }

  const path = `previews/${Date.now()}-${fileName}`;
  const data = await blobToBase64(blob);
  await Filesystem.writeFile({
    path,
    data,
    directory: Directory.Cache,
    recursive: true
  });
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
  await FileOpener.openFile({ path: uri, mimeType });
  return { platform: Capacitor.getPlatform(), fileName, uri };
};

export const openRemoteFile = async (url, options = {}) => {
  if (!url) throw new Error('File URL is required.');

  if (!isNativeMobile()) {
    const fileName = ensureExtension(options.fileName || 'document', options.mimeType);
    const previewKey = `${fileName}:${options.mimeType || ''}:${url}`;
    if (isDuplicateWebPreview(previewKey)) {
      return { platform: 'web', fileName, mode: 'deduplicated' };
    }

    const handled = requestInAppPreview({
      url,
      fileName,
      mimeType: options.mimeType || '',
      revokeOnClose: false
    });

    if (!handled) {
      window.location.assign(url);
      return { platform: 'web', fileName, mode: 'same-window' };
    }

    return { platform: 'web', fileName, mode: 'preview' };
  }

  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new Error(`Unable to load the file (${response.status}).`);
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const dispositionName = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
  const fileName = options.fileName || (dispositionName ? decodeURIComponent(dispositionName) : 'document');
  return openBlobFile(blob, {
    fileName,
    mimeType: options.mimeType || blob.type || response.headers.get('content-type')
  });
};

export const downloadBlobFile = (value, options = {}) => openBlobFile(value, {
  ...options,
  download: !isNativeMobile()
});
